/**
 * Illustrative merge-field substitution for the template editor.
 *
 * This is NOT the send path. The server resolves tokens per recipient and
 * decides what can go out — see `previewBulkSend`. Anything here exists only so
 * a coach can read the shape of the message while writing it, which is why it
 * fills every token from sample data rather than leaving gaps.
 */

export const SAMPLE_MERGE_VALUES: Record<string, string> = {
  firstName: "Jamie",
  lastName: "Ortiz",
  fullName: "Jamie Ortiz",
  email: "jamie.ortiz@example.com",
  phone: "(513) 555-0142",
  // Player fields resolve for a guardian too, which is why they read as the
  // player's rather than the recipient's.
  playerFirstName: "Jamie",
  playerLastName: "Ortiz",
  playerFullName: "Jamie Ortiz",
  position: "Shortstop",
  gradYear: "2027",
  classYear: "2027",
  school: "Elder High School",
  team: "Varsity",
  travelTeam: "Ohio Elite",
  city: "Cincinnati",
  state: "OH",
  gpa: "3.8",
};

/** Matches the server's token grammar in `MessagingTemplateRenderer`. */
const TOKEN_PATTERN = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

/**
 * Fills tokens with sample values. Unknown tokens are left visible rather than
 * blanked, so a typo reads as a typo in the preview instead of as a gap the
 * coach might not notice until the server rejects it.
 */
export const renderTemplatePreview = (body: string): string =>
  body.replace(TOKEN_PATTERN, (match, key: string) => {
    const sampleKey = Object.keys(SAMPLE_MERGE_VALUES).find(
      (candidate) => candidate.toLowerCase() === key.toLowerCase()
    );
    return sampleKey ? SAMPLE_MERGE_VALUES[sampleKey] : match;
  });

/** Tokens in the body, first-appearance order, deduplicated case-insensitively. */
export const extractTokens = (body: string): string[] => {
  const seen = new Set<string>();
  const tokens: string[] = [];

  for (const match of body.matchAll(TOKEN_PATTERN)) {
    const key = match[1];
    const lower = key.toLowerCase();
    if (seen.has(lower)) continue;
    seen.add(lower);
    tokens.push(key);
  }

  return tokens;
};
